/**
 * A bordered surface for one thing: a device, a share link, a backup, a plan (cockatrice-ui `src/ui/Card.jsx`, ported
 * to hyperscript; PLAN.md §0).
 * Slots: eyebrow, title, meta, actions (top right), children (body), footer.
 * `href` makes the whole card a link (interactive variant); then don't put other controls inside.
 * `tone`: "default" | "sunken" (panel colour) | "accent" | "warn" | "danger".
 * Tones add a coloured icon before the title; the border stays neutral. Never a coloured side stripe.
 */
import './css/card.css';
import { mergeProps, splitProps } from 'solid-js';
import Icon from './Icon.ts';
import type { IconName } from './Icon.ts';
import { component, dynamic, el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';

export interface CardProps extends Attributes {
  /** Card title. */
  readonly title?: Child;
  /** Heading element for the title. Default "h3". */
  readonly titleAs?: 'h2' | 'h3' | 'h4' | 'div' | undefined;
  /** Small ink-blue label above the title. */
  readonly eyebrow?: Child;
  /** Secondary line under the title. */
  readonly meta?: Child;
  /** Controls at the top right. */
  readonly actions?: Child;
  /** Buttons at the bottom, above a hairline. */
  readonly footer?: Child;
  /** Body. */
  readonly children?: Child;
  /** Makes the whole card a link. Then put no other controls inside. */
  readonly href?: string | undefined;
  /** Surface or left rule. Default "default". */
  readonly tone?: 'default' | 'sunken' | 'accent' | 'warn' | 'danger' | undefined;
  /** Inner spacing. Default "md". */
  readonly padding?: 'md' | 'sm' | 'none' | undefined;
  /** Ink-blue outline and aria-current. */
  readonly selected?: boolean | undefined;
  /** Extra classes. */
  readonly class?: string | undefined;
}

const TONE_ICON: Readonly<Partial<Record<string, IconName>>> = { accent: 'check', warn: 'warn', danger: 'warn' };

export default function Card(props: CardProps): Child {
  const [local, rest] = splitProps(props, ['class', 'href', 'eyebrow', 'title', 'titleAs', 'meta', 'actions', 'footer', 'children', 'tone', 'padding', 'selected']);
  const hasHead = (): boolean => Boolean(local.title) || Boolean(local.eyebrow) || Boolean(local.meta) || Boolean(local.actions);
  const toneIcon = (): IconName | undefined => (local.tone === undefined ? undefined : TONE_ICON[local.tone]);
  return dynamic(
    () => (local.href !== undefined && local.href !== '' ? 'a' : 'div'),
    mergeProps(
      {
        href: () => local.href,
        class: () =>
          [
            'ui-card',
            local.href !== undefined && local.href !== '' && 'ui-card--link',
            local.tone !== undefined && local.tone !== 'default' && `ui-card--${local.tone}`,
            local.padding === 'sm' && 'ui-card--pad-sm',
            local.padding === 'none' && 'ui-card--pad-none',
            local.class,
          ]
            .filter(Boolean)
            .join(' '),
        'aria-current': () => (local.selected === true ? 'true' : undefined),
      },
      rest,
    ),
    when(hasHead, () =>
      el(
        'div',
        { class: 'ui-card__head' },
        el(
          'div',
          { class: 'ui-card__heading' },
          when(
            () => Boolean(local.eyebrow),
            () => el('div', { class: 'ui-card__eyebrow' }, () => local.eyebrow),
          ),
          when(
            () => Boolean(local.title),
            () =>
              dynamic(
                () => local.titleAs ?? 'h3',
                { class: 'ui-card__title' },
                when(
                  () => toneIcon() !== undefined,
                  () =>
                    component(Icon, {
                      get name() {
                        return toneIcon() ?? 'check';
                      },
                      size: 'sm',
                      class: 'ui-card__tone-icon',
                    }),
                ),
                () => local.title,
              ),
          ),
          when(
            () => Boolean(local.meta),
            () => el('div', { class: 'ui-card__meta' }, () => local.meta),
          ),
        ),
        when(
          () => Boolean(local.actions),
          () => el('div', { class: 'ui-card__actions' }, () => local.actions),
        ),
      ),
    ),
    when(
      () => Boolean(local.children),
      () => el('div', { class: 'ui-card__body' }, () => local.children),
    ),
    when(
      () => Boolean(local.footer),
      () => el('div', { class: 'ui-card__footer' }, () => local.footer),
    ),
  );
}
